import {
  GetStaticPaths,
  GetStaticProps,
  GetStaticPropsContext,
  NextPage,
} from "next";

import Layout from "@/components/layouts/default";
import { ISR_REVALIDATE_INTERVAL_SECONDS } from "@/constants/next-page";
import { CustomerSuccessStoryModule } from "@/modules/customer-success/story";
import { CustomerSuccessStoryI } from "@/interfaces/strapi/collection-types";
import { StrapiAPI } from "@/api/strapi-api";
import { getEntityAttributesFromStrapiList } from "@/helpers/strapi";
import Router from "@/helpers/router";
import { getArticleMetaTags } from "@/helpers/seo";
import { OTHER_ARTICLES_LIMIT } from "@/constants/strapi";

interface CustomerSuccessStoryPagePropsI {
  story?: CustomerSuccessStoryI;
  otherStories?: CustomerSuccessStoryI[];
  slug: string;
}

const CustomerSuccessStoryPage: NextPage<CustomerSuccessStoryPagePropsI> = ({
  story,
  otherStories,
  slug,
}) => (
  <Layout
    meta={{
      canonical: Router.customerSuccessStories(slug),
      ...getArticleMetaTags(story),
    }}
  >
    <CustomerSuccessStoryModule story={story} otherStories={otherStories} />
  </Layout>
);

export default CustomerSuccessStoryPage;

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    // https://nextjs.org/docs/pages/api-reference/functions/get-static-paths
    paths: [],
    // Enable statically generating additional pages
    fallback: true,
  };
};

export const getStaticProps: GetStaticProps = async (
  ctx: GetStaticPropsContext
) => {
  const slug = ctx.params?.slug as string;
  let story: CustomerSuccessStoryI | undefined;
  let otherStories: CustomerSuccessStoryI[] | undefined;

  if (!slug) {
    return {
      props: {
        story: null,
      },
      revalidate: ISR_REVALIDATE_INTERVAL_SECONDS,
    };
  }

  try {
    const api = StrapiAPI();
    const [featuredStoryResponse, otherStoriesResponse] = await Promise.all([
      api.getCustomerSuccessStory(slug),
      api.getCustomerSuccessStories(0, {
        "pagination[pageSize]": OTHER_ARTICLES_LIMIT,
        "filters[slug][$ne]": slug,
      }),
    ]);

    if (featuredStoryResponse.status === 200) {
      story = getEntityAttributesFromStrapiList(featuredStoryResponse.data)[0];
    }

    if (otherStoriesResponse.status === 200) {
      otherStories = getEntityAttributesFromStrapiList(
        otherStoriesResponse.data
      );
    }
  } catch (e) {
    console.error("Failed to load customer success story", e);
  }

  const staticProps: {
    props: CustomerSuccessStoryPagePropsI;
    revalidate: number;
  } = {
    props: {
      slug,
    },
    revalidate: ISR_REVALIDATE_INTERVAL_SECONDS,
  };

  if (story) {
    staticProps.props.story = story;
  }

  if (otherStories) {
    staticProps.props.otherStories = otherStories;
  }

  return staticProps;
};
